import {IContractEditData} from "./IContractData";
import {ServiceAdapter} from "./ServiceAdapter";
import {IAction} from "./IServiceAdapter";
import {ContractState} from "../ContractDataModels";

export type XBidContractStatus = "EXPIRED" | "OPEN" | "CLOSED" | "NEW" | "FROZEN";
export type XBidLoginStatus = "CONNECTED" | "DISCONNECTED" | "LOGGED_IN" | "LOGGED_OUT";

interface IXBidLoginStatus {
    user: string; // The user is indifferent, we use single xbid user for all the logged in operator accounts
    status: XBidLoginStatus;
}

interface IXBidChangeRequestItem {
    deliveryAreaId: string;
    contractId: string;
}
type SaveMethod = (changedItems: IXBidChangeRequestItem[]) => Promise<any>;

export class XBidServiceAdapter extends ServiceAdapter<XBidContractStatus> {
    private static readonly LOGIN_STATE_REFRESH_INTERVAL = 10000;
    protected readonly contractsApiPath = "contractManagement/lts";

    readonly availableActions: IAction<XBidContractStatus>[] = [{ label: "Open", status: "OPEN" }, { label: "Block", status: "FROZEN" }];

    private refreshStateIntervalId = this.isXbidTenant() ?
        window.setInterval(this.updateXbidLoginStatus.bind(this), XBidServiceAdapter.LOGIN_STATE_REFRESH_INTERVAL) : null;

    private _loginStatus: XBidLoginStatus = "DISCONNECTED";

    private ongoingRequests: Promise<any>[] = [];
    private initialStatusRetrieved = this.isXbidTenant() ? this.updateXbidLoginStatus() : null;

    private readonly saveMethods: { [newState: string]: SaveMethod } = {
        FROZEN: this.blockContract.bind(this),
        OPEN: this.openContract.bind(this)
    };

    private removeOngoingRequest(request: Promise<any>) {
        const i = this.ongoingRequests.findIndex(ongoingRequest => ongoingRequest === request);
        this.ongoingRequests.splice(i, 1);
    }

    private makeRequest<TResponse>(request: Promise<TResponse>): Promise<TResponse> {
        this.ongoingRequests.push(request);
        request
            .then(response => this.removeOngoingRequest(request))
            .catch(error => this.removeOngoingRequest(request));

        return request;
    }

    private updateXbidLoginStatus(): Promise<IXBidLoginStatus> {
        const uri = "auth/login/status";

        const request = this.makeRequest<IXBidLoginStatus>(super.get<IXBidLoginStatus>(uri, {"requestedTenant": this.getCurrentTenantId()}));
        request
            .then(loginStatus => {
                this._loginStatus = loginStatus.status;
            });

        return request;
    }


    get loginStatus(): XBidLoginStatus {
        return this._loginStatus;
    }

    login(): Promise<void> {
        if (this.loggedIn) {
            return Promise.resolve();
        }

        return this.makeRequest<void>(super.post<void>("auth/login", {}, {"requestedTenant": this.getCurrentTenantId()}));
    }

    connect(): Promise<void> {
        if (this.connected) {
            return Promise.resolve();
        }

        return this.makeRequest<void>(super.post<void>("auth/connect", {}, {"requestedTenant": this.getCurrentTenantId()}));
    }

    disconnect(): Promise<void> {
        if (this.disconnected) {
            return Promise.resolve();
        }

        return this.makeRequest<void>(super.post<void>("auth/disconnect", {}, {"requestedTenant": this.getCurrentTenantId()}));
    }

    logout(): Promise<void> {
        if (this.loggedOut) {
            return Promise.resolve();
        }

        return this.makeRequest<void>(super.post<void>("auth/logout", {}, {"requestedTenant": this.getCurrentTenantId()}));
    }

    get loggedIn(): boolean {
        return this._loginStatus === "LOGGED_IN";
    }

    get loggedOut(): boolean {
        return this._loginStatus === "LOGGED_OUT";
    }

    get connected(): boolean {
        return this._loginStatus === "CONNECTED";
    }

    get disconnected(): boolean {
        return this._loginStatus === "DISCONNECTED";
    }

    get statusAvailable(): Promise<IXBidLoginStatus> {
        return this.initialStatusRetrieved;
    }

    get hasOngoingRequest(): boolean {
        return this.ongoingRequests.length > 0;
    }

    save(changedContractAreas: IContractEditData[]): Promise<any> {
        return this.makeRequest(Promise.all([
            this.saveContractAreaStatusChanges(changedContractAreas, "OPEN"),
            this.saveContractAreaStatusChanges(changedContractAreas, "FROZEN")
        ]));
    }

    contractManagementRequest(action: ContractState): Promise<any> {
        return super.post<any>(this.contractsApiPath, {
            "contractManagementAction": action,
            "contracts": [],
            "tenants": [
                this.getCurrentTenantId()
            ]
        });
    }

    private saveContractAreaStatusChanges(changedContractAreas: IContractEditData[], contractStatus: XBidContractStatus): Promise<any> {
        const changesToSave: IXBidChangeRequestItem[] = changedContractAreas.filter(change => change.contractStatus === contractStatus).map(change => ({
            deliveryAreaId: change.marketAreaCode,
            contractId: change.id,
            tenant: change.tenantId
        }));

        return changesToSave.length
            ? this.saveMethods[contractStatus](changesToSave)
            : Promise.resolve();
    }

    private blockContract(changedItems: IXBidChangeRequestItem[]): Promise<any> {
        return super.post(this.contractsApiPath, {
            "contractManagementAction": ContractState.CLOSE,
            "contracts": changedItems,
            "tenants": []
        });
    }

    private openContract(changedItems: IXBidChangeRequestItem[]): Promise<any> {
        return super.post(this.contractsApiPath, {
            "contractManagementAction": ContractState.OPEN,
            "contracts": changedItems,
            "tenants": []
        });
    }

    getContracts() {
        return this.makeGetContractsRequest();
    }

    isContractStatusEditable(status: XBidContractStatus): boolean {
        switch (status) {
            case "NEW":
            case "OPEN":
            case "FROZEN":
                return true;
            default:
                return false;
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/ContractManagement/Services/XBidServiceAdapter.ts